import { useQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Users } from 'react-feather';

import Button from '@/componentsv2/Button';

import EditMembers from './Dialog';
import { FETCH_PROJECT_MEMBERS } from './SettingsMenu';

interface ManageMembersButtonProps {
  // Forwarded to EditMembers untouched — no field is read here.
  viewer: Record<string, unknown>;
  project: { id: string };
}

// The single project-level "Manage Members" action (ENG-5314), replacing the
// project gear menu. White bordered button (v2 secondary base overridden to a
// card surface + border) to pair with the header stage badge. Reuses the
// members query + EditMembers dialog that SettingsMenu uses.
export default function ManageMembersButton({
  viewer,
  project,
}: ManageMembersButtonProps): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const { data, error, refetch } = useQuery(FETCH_PROJECT_MEMBERS, {
    variables: { id: project.id },
  });

  // Don't fail silently — the dialog would otherwise open with an empty list.
  useEffect(() => {
    if (error) {
      enqueueSnackbar("Couldn't load project members. Please try again.", { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const membersSorted = useMemo(
    () =>
      data?.project?.members
        ?.filter((m: any) => ['active', 'denied'].includes(m.state))
        ?.sort((a: any, b: any) => {
          const roles = ['manager', 'associate', 'owner', 'member', 'viewer'];
          return roles.indexOf(a.role) - roles.indexOf(b.role);
        }) || [],
    [data?.project?.members]
  );

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <Button
        variant="secondary"
        size="small"
        startIcon={Users}
        onClick={handleOpen}
        className="border border-surface-border !bg-surface-card !shadow-none hover:!bg-surface-secondary"
      >
        Manage Members
      </Button>
      <EditMembers
        viewer={viewer}
        open={open}
        onClose={handleClose}
        onSubmit={() => refetch()}
        project={project}
        initialValues={{ members: membersSorted }}
      />
    </>
  );
}
